<template>
  <div class="stripe-data-card">
    <div
      v-if="loading"
      class="flex items-center justify-center py-12">
      <i class="fas fa-spinner fa-spin text-2xl text-indigo-600" />
    </div>

    <div
      v-else-if="stripeData"
      class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Total Revenue Card -->
        <div class="metric-card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div class="metric-icon bg-green-600">
            <i class="fas fa-dollar-sign" />
          </div>
          <div class="metric-content">
            <div class="metric-label">
              Total Revenue
            </div>
            <div class="metric-value text-green-700">
              ${{ formatCurrency(stripeData.totalPaid) }}
            </div>
          </div>
        </div>

        <!-- MRR Card -->
        <div class="metric-card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div class="metric-icon bg-blue-600">
            <i class="fas fa-sync-alt" />
          </div>
          <div class="metric-content">
            <div class="metric-label">
              Monthly Recurring
            </div>
            <div class="metric-value text-blue-700">
              ${{ formatCurrency(stripeData.monthlyRecurringRevenue) }}
            </div>
          </div>
        </div>

        <!-- Subscriptions Card -->
        <div class="metric-card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div class="metric-icon bg-purple-600">
            <i class="fas fa-calendar-check" />
          </div>
          <div class="metric-content">
            <div class="metric-label">
              Active Subscriptions
            </div>
            <div class="metric-value text-purple-700">
              {{ stripeData.activeSubscriptionsCount }}
            </div>
          </div>
        </div>

        <!-- Status Card -->
        <div
          :class="getStatusCardClass()"
          class="metric-card">
          <div
            :class="getStatusIconClass()"
            class="metric-icon">
            <i :class="getStatusIcon()" />
          </div>
          <div class="metric-content">
            <div class="metric-label">
              Account Status
            </div>
            <div
              :class="getStatusTextClass()"
              class="metric-value">
              {{ getStatusText() }}
            </div>
          </div>
        </div>
      </div>

      <!-- Discounts Banner -->
      <div
        v-if="hasActiveDiscount"
        class="discount-banner active">
        <div class="discount-icon">
          <i class="fas fa-tag" />
        </div>
        <div class="discount-content">
          <div class="discount-title">
            Active Discounts
          </div>
          <div class="discount-details">
            <span class="discount-count">{{ activeDiscountsCount }} discount{{ activeDiscountsCount > 1 ? 's' : '' }}</span>
            saving <span class="discount-amount">${{ formatCurrency(totalDiscountAmount) }}/month</span>
          </div>
        </div>
      </div>

      <!-- No Discounts Banner -->
      <div
        v-else
        class="discount-banner inactive">
        <div class="discount-icon inactive">
          <i class="fas fa-tag" />
        </div>
        <div class="discount-content">
          <div class="discount-title text-gray-600">
            No Active Discounts
          </div>
          <div class="discount-details text-gray-500">
            No coupons or promotional discounts are currently applied
          </div>
        </div>
      </div>

      <!-- Detailed Information Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Customer Information -->
        <div class="info-section">
          <h4 class="info-section-title">
            <i class="fas fa-user-circle text-indigo-600 mr-2" />
            Customer Information
          </h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Customer ID</span>
              <span class="info-value font-mono text-sm">{{ stripeData.stripeCustomerId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Email</span>
              <span class="info-value">{{ stripeData.customerEmail || 'Not set' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Customer Since</span>
              <span class="info-value">{{ formatFullDate(stripeData.customerCreatedAt) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Currency</span>
              <span class="info-value uppercase">{{ stripeData.customerCurrency || 'USD' }}</span>
            </div>
          </div>
        </div>

        <!-- Financial Summary -->
        <div class="info-section">
          <h4 class="info-section-title">
            <i class="fas fa-chart-line text-green-600 mr-2" />
            Financial Summary
          </h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Total Invoiced</span>
              <span class="info-value font-medium">
                ${{ formatCurrency(stripeData.totalInvoiced) }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Outstanding Balance</span>
              <span
                :class="stripeData.totalOutstanding > 0 ? 'text-red-600 font-bold' : ''"
                class="info-value">
                ${{ formatCurrency(stripeData.totalOutstanding) }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Total Invoices</span>
              <span class="info-value">{{ stripeData.totalInvoicesCount }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Failed Payments</span>
              <span
                :class="stripeData.failedPaymentsCount > 0 ? 'text-red-600 font-medium' : ''"
                class="info-value">
                {{ stripeData.failedPaymentsCount }}
                <span
                  v-if="stripeData.lastFailedPaymentDate"
                  class="text-xs text-gray-500 block">
                  Last: {{ formatDate(stripeData.lastFailedPaymentDate) }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <!-- Payment Activity -->
        <div class="info-section">
          <h4 class="info-section-title">
            <i class="fas fa-credit-card text-blue-600 mr-2" />
            Payment Activity
          </h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Last Payment</span>
              <span class="info-value">
                {{ stripeData.lastPaymentDate ? formatFullDate(stripeData.lastPaymentDate) : 'Never' }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Last Amount</span>
              <span class="info-value">
                {{ stripeData.lastPaymentAmount ? '$' + formatCurrency(stripeData.lastPaymentAmount) : 'N/A' }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Last Invoice</span>
              <span class="info-value">
                {{ stripeData.lastInvoiceDate ? formatFullDate(stripeData.lastInvoiceDate) : 'Never' }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Balance</span>
              <span
                :class="getBalanceClass()"
                class="info-value">
                ${{ formatCurrency(Math.abs(stripeData.customerBalance || 0) / 100) }}
                {{ stripeData.customerBalance > 0 ? ' (Credit)' : stripeData.customerBalance < 0 ? ' (Due)' : '' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Payment Methods -->
        <div
          v-if="stripeData.paymentMethodsCount > 0"
          class="info-section">
          <h4 class="info-section-title">
            <i class="fas fa-wallet text-indigo-600 mr-2" />
            Payment Methods
          </h4>
          <div class="space-y-3">
            <div
              v-for="method in stripeData.paymentMethodsData"
              :key="method.id"
              class="payment-method-item">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <i
                    :class="getPaymentMethodIcon(method.type)"
                    class="text-gray-600" />
                  <div>
                    <div class="font-medium">
                      {{ method.displayName || formatPaymentMethod(method) }}
                    </div>
                    <div class="text-sm text-gray-500">
                      {{ method.exp ? 'Expires ' + method.exp : '' }}
                      {{ method.billingDetails?.name ? '• ' + method.billingDetails.name : '' }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="isDefaultPaymentMethod(method)"
                  class="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                  Default
                </div>
              </div>
            </div>
            <div
              v-if="stripeData.defaultPaymentMethodBrand && stripeData.paymentMethodsData?.length === 0"
              class="text-sm text-gray-600 italic">
              Default: {{ stripeData.defaultPaymentMethodBrand }} •••• {{ stripeData.defaultPaymentMethodLast4 }}
            </div>
          </div>
        </div>

        <!-- Subscription Details -->
        <div
          v-if="stripeData.activeSubscriptionsCount > 0"
          class="info-section">
          <h4 class="info-section-title">
            <i class="fas fa-calendar-alt text-purple-600 mr-2" />
            Subscription Details
          </h4>
          <div class="space-y-3">
            <div
              v-for="sub in stripeData.subscriptionsData"
              :key="sub.id"
              class="subscription-item">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span
                    :class="getSubscriptionStatusClass(sub.status)"
                    class="subscription-status">
                    {{ sub.status }}
                  </span>
                  <span class="text-sm text-gray-600">
                    {{ formatSubscriptionPeriod(sub) }}
                  </span>
                </div>
                <span class="font-medium">
                  ${{ calculateSubscriptionAmount(sub) }}/mo
                </span>
              </div>
              <div
                v-if="sub.items && sub.items.length > 0"
                class="mt-2 text-sm text-gray-600">
                <div
                  v-for="item in sub.items"
                  :key="item.id"
                  class="ml-4">
                  • {{ item.quantity }}x {{ getProductName(item) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Discount Details -->
        <div class="info-section lg:col-span-2">
          <h4 class="info-section-title">
            <i class="fas fa-tags text-amber-600 mr-2" />
            Discount & Coupon Details
          </h4>
          <div v-if="customerDiscountData.length > 0">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="(discountInfo, index) in customerDiscountData"
                :key="index"
                class="discount-detail-card">
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-2">
                      <span
                        :class="discountInfo.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'"
                        class="text-xs font-medium px-2 py-0.5 rounded-full">
                        {{ discountInfo.active ? 'Active' : 'Inactive' }}
                      </span>
                      <span class="text-xs text-gray-500 font-medium uppercase">
                        {{ discountInfo.type }} Discount
                      </span>
                    </div>
                    <div class="font-medium text-gray-900">
                      {{ formatDiscountName(discountInfo.discount) }}
                    </div>
                    <div class="mt-1 text-sm text-gray-600">
                      {{ formatDiscountAmount(discountInfo.discount) }}
                      <span v-if="discountInfo.discount.coupon.duration !== 'forever'">
                        • {{ formatDuration(discountInfo.discount.coupon.duration) }}
                      </span>
                    </div>
                    <div
                      v-if="discountInfo.discount.end"
                      class="mt-1 text-xs text-gray-500">
                      Expires: {{ formatFullDate(new Date(discountInfo.discount.end * 1000)) }}
                    </div>
                  </div>
                  <div class="ml-4">
                    <i class="fas fa-tag text-2xl text-amber-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="text-center py-8 bg-gray-50 rounded-lg">
            <i class="fas fa-tag text-4xl text-gray-300 mb-3" />
            <p class="text-gray-500 text-sm">
              No discount history available
            </p>
            <p class="text-gray-400 text-xs mt-1">
              Applied coupons and promotional discounts will appear here
            </p>
          </div>
        </div>
      </div>

      <!-- Invoices Section -->
      <div
        v-if="showInvoices"
        class="mt-6">
        <StripeInvoicesTable :organization-id="organizationId" />
      </div>

      <!-- Sync Information -->
      <div class="sync-info">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-500">
            <i class="fas fa-sync-alt mr-1" />
            Last synced: {{ formatRelativeTime(stripeData.lastSyncedAt) }}
          </div>
          <button
            class="sync-button"
            :disabled="syncing"
            @click="syncStripeData">
            <i :class="syncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" />
            {{ syncing ? 'Syncing...' : 'Sync Now' }}
          </button>
        </div>
        <div
          v-if="stripeData.syncError"
          class="mt-2 text-sm text-red-600">
          <i class="fas fa-exclamation-triangle mr-1" />
          Last sync error: {{ stripeData.syncError }}
        </div>
      </div>
    </div>

    <div
      v-else
      class="no-data">
      <i class="fas fa-credit-card text-4xl text-gray-300 mb-4" />
      <p class="text-gray-500">
        No Stripe data available
      </p>
      <button
        class="sync-button mt-4"
        :disabled="syncing"
        @click="syncStripeData">
        <i :class="syncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" />
        {{ syncing ? 'Syncing...' : 'Sync Stripe Data' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'
import StripeInvoicesTable from './stripe_invoices_table.vue'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  showInvoices: {
    type: Boolean,
    default: true
  }
})

// State
const loading = ref(true)
const syncing = ref(false)
const stripeData = ref(null)

// Computed properties for discounts
const hasActiveDiscount = computed(() => stripeData.value?.hasActiveDiscount || false)

const activeDiscountsCount = computed(() => stripeData.value?.activeDiscountsCount || 0)

const totalDiscountAmount = computed(() => stripeData.value?.totalDiscountAmount || 0)

const customerDiscountData = computed(() => stripeData.value?.customerDiscountData || [])

// Fetch stripe data
const fetchStripeData = async() => {
  loading.value = true
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/stripe_data`)
    stripeData.value = response.data
  } catch (error) {
    // Error fetching stripe data
    mdtoast.error('Failed to load Stripe data', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

// Sync stripe data
const syncStripeData = async() => {
  syncing.value = true
  try {
    const response = await axios.post(`/ajax/organizations/${props.organizationId}/sync_stripe`)
    if (response.data.success) {
      mdtoast.success('Stripe data synced successfully', { duration: 3000 })
      await fetchStripeData()
    } else {
      mdtoast.error(response.data.error || 'Failed to sync Stripe data', { duration: 3000 })
    }
  } catch (error) {
    // Error syncing stripe data
    mdtoast.error('Failed to sync Stripe data', { duration: 3000 })
  } finally {
    syncing.value = false
  }
}

// Formatting helpers
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0.00'
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatFullDate = (dateString) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatDate = (dateString) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`

  return formatFullDate(dateString)
}

// Status helpers
const getStatusCardClass = () => {
  if (!stripeData.value) return 'bg-gray-50 border-gray-200'
  if (stripeData.value.customerDelinquent) return 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
  if (stripeData.value.totalOutstanding > 0) return 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'
  return 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
}

const getStatusIconClass = () => {
  if (!stripeData.value) return 'bg-gray-600'
  if (stripeData.value.customerDelinquent) return 'bg-red-600'
  if (stripeData.value.totalOutstanding > 0) return 'bg-yellow-600'
  return 'bg-green-600'
}

const getStatusIcon = () => {
  if (!stripeData.value) return 'fas fa-question'
  if (stripeData.value.customerDelinquent) return 'fas fa-exclamation-triangle'
  if (stripeData.value.totalOutstanding > 0) return 'fas fa-clock'
  return 'fas fa-check-circle'
}

const getStatusText = () => {
  if (!stripeData.value) return 'Unknown'
  if (stripeData.value.customerDelinquent) return 'Delinquent'
  if (stripeData.value.totalOutstanding > 0) return 'Payment Due'
  return 'Good Standing'
}

const getStatusTextClass = () => {
  if (!stripeData.value) return 'text-gray-700'
  if (stripeData.value.customerDelinquent) return 'text-red-700'
  if (stripeData.value.totalOutstanding > 0) return 'text-yellow-700'
  return 'text-green-700'
}

const getBalanceClass = () => {
  if (!stripeData.value || !stripeData.value.customerBalance) return ''
  if (stripeData.value.customerBalance > 0) return 'text-green-600 font-medium'
  if (stripeData.value.customerBalance < 0) return 'text-red-600 font-medium'
  return ''
}

const getSubscriptionStatusClass = (status) => {
  const classes = {
    active: 'bg-green-100 text-green-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-red-100 text-red-700',
    canceled: 'bg-gray-100 text-gray-700',
    unpaid: 'bg-red-100 text-red-700'
  }
  return `subscription-status ${classes[status] || 'bg-gray-100 text-gray-700'}`
}

const formatSubscriptionPeriod = (sub) => {
  if (!sub.currentPeriodStart || !sub.currentPeriodEnd) return ''
  const start = new Date(sub.currentPeriodStart * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const end = new Date(sub.currentPeriodEnd * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${start} - ${end}`
}

const calculateSubscriptionAmount = (sub) => {
  if (!sub.latestInvoice || !sub.latestInvoice.amountPaid) return '0.00'
  return (sub.latestInvoice.amountPaid / 100).toFixed(2)
}

const getProductName = (item) => {
  if (item.price && item.price.nickname) return item.price.nickname
  if (item.price && item.price.id) return `Price: ${item.price.id}`
  return 'Subscription'
}

// Payment method helpers
const getPaymentMethodIcon = (type) => {
  const icons = {
    card: 'fas fa-credit-card',
    bank_account: 'fas fa-university',
    us_bank_account: 'fas fa-university',
    ach_debit: 'fas fa-university',
    sepa_debit: 'fas fa-euro-sign',
    ideal: 'fas fa-money-check',
    paypal: 'fab fa-paypal'
  }
  return icons[type] || 'fas fa-dollar-sign'
}

const formatPaymentMethod = (method) => {
  if (method.type === 'card' && method.card) {
    return `${method.card.brand?.charAt(0).toUpperCase()}${method.card.brand?.slice(1)} •••• ${method.card.last4}`
  }
  if ((method.type === 'us_bank_account' || method.type === 'bank_account') && method.bankAccount) {
    return `${method.bankAccount.bankName} •••• ${method.bankAccount.last4}`
  }
  return method.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

const isDefaultPaymentMethod = (method) => {
  if (!stripeData.value.defaultPaymentMethodLast4) return false
  return method.last4 === stripeData.value.defaultPaymentMethodLast4 &&
         method.brand === stripeData.value.defaultPaymentMethodBrand
}

// Discount formatting helpers
const formatDiscountName = (discount) => {
  if (discount && discount.coupon && discount.coupon.name) {
    return discount.coupon.name
  }
  return discount?.coupon?.id || 'Discount'
}

const formatDiscountAmount = (discount) => {
  if (!discount || !discount.coupon) return ''

  if (discount.coupon.percent_off) {
    return `${discount.coupon.percent_off}% off`
  } if (discount.coupon.amount_off) {
    const amount = (discount.coupon.amount_off / 100).toFixed(2)
    const currency = (discount.coupon.currency || 'usd').toUpperCase()
    return `$${amount} ${currency} off`
  }

  return ''
}

const formatDuration = (duration) => {
  const durations = {
    once: 'One time',
    repeating: 'Repeating',
    forever: 'Forever'
  }
  return durations[duration] || duration
}

onMounted(() => {
  fetchStripeData()
})
</script>

<style scoped>
.stripe-data-card {
  @apply bg-white rounded-lg;
}

.metric-card {
  @apply relative overflow-hidden rounded-lg border p-6 transition-all duration-200 hover:shadow-md;
}

.metric-icon {
  @apply absolute -top-2 -right-2 rounded-full p-3 text-white opacity-90;
}

.metric-content {
  @apply relative z-10;
}

.metric-label {
  @apply text-sm font-medium text-gray-600;
}

.metric-value {
  @apply text-2xl font-bold;
}

.info-section {
  @apply bg-gray-50 rounded-lg p-6 border border-gray-200;
}

.info-section-title {
  @apply text-lg font-semibold text-gray-900 mb-4 flex items-center;
}

.info-grid {
  @apply grid grid-cols-2 gap-4;
}

.info-item {
  @apply flex flex-col space-y-1;
}

.info-label {
  @apply text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.info-value {
  @apply text-sm text-gray-900;
}

.subscription-item {
  @apply bg-white rounded-lg border border-gray-200 p-4;
}

.payment-method-item {
  @apply bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors;
}

.subscription-status {
  @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium;
}

.sync-info {
  @apply bg-gray-50 rounded-lg p-4 border border-gray-200;
}

.sync-button {
  @apply inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium
         rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2
         focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
}

.no-data {
  @apply flex flex-col items-center justify-center py-12 text-center;
}

.discount-banner {
  @apply border rounded-lg p-4 flex items-center gap-4;
}

.discount-banner.active {
  @apply bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200;
}

.discount-banner.inactive {
  @apply bg-gray-50 border-gray-200;
}

.discount-icon {
  @apply rounded-full p-3 flex-shrink-0;
}

.discount-icon:not(.inactive) {
  @apply bg-amber-500 text-white;
}

.discount-icon.inactive {
  @apply bg-gray-300 text-gray-500;
}

.discount-content {
  @apply flex-1;
}

.discount-title {
  @apply text-lg font-semibold text-gray-900;
}

.discount-details {
  @apply text-sm text-gray-700 mt-1;
}

.discount-count {
  @apply font-medium text-amber-700;
}

.discount-amount {
  @apply font-bold text-amber-700;
}

.discount-detail-card {
  @apply bg-white rounded-lg border border-gray-200 p-4 hover:border-amber-300 transition-colors;
}
</style>
